import { FileInput, Bot, CalendarCheck, ArrowDown } from 'lucide-react';

export const HowItWorks = () => {
  const steps = [
    {
      icon: FileInput,
      number: "01",
      title: "Lead Comes In",
      description: "A homeowner fills out your website form or calls your number. Day or night, weekend or holiday—doesn't matter.",
      detail: "Our system captures every inquiry instantly, no matter the source."
    },
    {
      icon: Bot,
      number: "02",
      title: "AI Qualifies & Responds",
      description: "Within seconds, our AI responds via text or call. It asks the right questions, qualifies the lead, and handles objections professionally.",
      detail: "No scripts to set up. It learns your business and speaks like your best rep."
    },
    {
      icon: CalendarCheck,
      number: "03",
      title: "Inspection Gets Booked",
      description: "Qualified leads are booked directly into your calendar. Your sales team shows up ready to close, not chase.",
      detail: "Syncs with your existing calendar. No double-bookings, no scheduling conflicts."
    }
  ];

  return (
    <section id="how-it-works-section" className="py-20 md:py-28 bg-slate-50" data-testid="how-it-works-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="text-orange-600 font-semibold text-sm uppercase tracking-wider mb-3">
            Simple Process
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight mb-6">
            From Lead to Booked in Minutes
          </h2>
          <p className="text-slate-600 text-lg max-w-2xl mx-auto leading-relaxed">
            No complicated setup. No tech headaches. We handle everything while you focus on closing jobs.
          </p>
        </div>

        {/* Steps Timeline */}
        <div className="max-w-3xl mx-auto">
          {steps.map((step, index) => (
            <div key={index} className="relative" data-testid={`step-${index + 1}`}>
              {/* Connector Line */}
              {index < steps.length - 1 && (
                <div className="absolute left-8 top-24 w-0.5 h-24 bg-slate-300 hidden md:block"></div>
              )}
              
              <div className="flex gap-6 md:gap-8 mb-12">
                {/* Icon & Number */}
                <div className="flex-shrink-0">
                  <div className="relative">
                    <div className="w-16 h-16 rounded bg-white border-2 border-slate-200 flex items-center justify-center shadow-sm">
                      <step.icon className="w-7 h-7 text-slate-700" />
                    </div>
                    <span className="absolute -top-2 -right-2 bg-orange-600 text-white text-xs font-bold px-2 py-1 rounded-sm">
                      {step.number}
                    </span>
                  </div>
                </div>
                
                {/* Content */}
                <div className="flex-1 bg-white rounded-sm border border-slate-200 p-6 md:p-8 shadow-sm hover:shadow-md transition-shadow">
                  <h3 className="font-heading text-xl md:text-2xl font-bold text-slate-900 mb-3">
                    {step.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed mb-4">
                    {step.description}
                  </p>
                  <p className="text-slate-500 text-sm border-l-2 border-orange-400 pl-3">
                    {step.detail}
                  </p>
                </div>
              </div>

              {/* Arrow between steps (mobile) */}
              {index < steps.length - 1 && (
                <div className="flex justify-center mb-8 md:hidden">
                  <ArrowDown className="w-6 h-6 text-slate-300" />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Bottom Note */}
        <div className="mt-12 text-center">
          <div className="inline-flex items-center gap-3 bg-white border border-slate-200 rounded-sm px-6 py-4 shadow-sm">
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
            <p className="text-slate-700 font-medium">
              Average setup time: <span className="font-bold">48 hours</span> — Most clients are live within 2 days
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
